import { CaptionStyle, Stack, TinyTitleBoldStyle } from '@kvdbil/components';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import React from 'react';
import styled from 'styled-components';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import {
  BiddingViewData,
  FixedPriceReservationTipsData
} from '~/App/shared/interfaces/store/CmsData';
import {
  BodyTextSkeleton,
  HeadlineSkeleton
} from '~/App/shared/components/Skeletons';
import {
  BiddingDocument,
  BiddingQuery,
  FixedPriceReservationTipsDocument,
  FixedPriceReservationTipsQuery,
  Scalars
} from '~/config/generated/graphql';
import { inclVATtoExclVAT } from '~/helpers/currency';

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.background.sand};
  padding: 1rem;
  margin-top: 1rem;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    ${TinyTitleBoldStyle}
    text-align: center;
    margin-top: 1rem;

    &:first-child {
      margin-top: 0;
    }
  }

  & ul {
    margin: 8px 0 0 0;
    padding-left: 16px;
    list-style-type: initial;
  }

  & li {
    text-align: start;
    ${CaptionStyle}
    & > p {
      ${CaptionStyle}
    }
  }
  & li::marker {
    font-size: 10px;
  }
`;

const TipsWrapper = styled.div``;

/**
 * Matches the excl. VAT parenthesis, e.g. "(${biddingFeeExclVAT} kronor exkl
 * moms)", including the whitespace in front of it. Only the placeholder is
 * matched, so it works for every locale of the CMS text.
 */
const EXCL_VAT_PARENTHESIS = /\s*\(\s*\$\{biddingFeeExclVAT\}[^)]*\)/g;

type FeeValues = {
  formattedFee: string;
  formattedFeeExclVAT: string;
  hasDeductibleVat: boolean;
};

const insertFees = (
  text: string,
  { formattedFee, formattedFeeExclVAT, hasDeductibleVat }: FeeValues
) => {
  const textForVat = hasDeductibleVat
    ? text
    : text.replace(EXCL_VAT_PARENTHESIS, '');

  return textForVat
    .replace('${biddingFee}', formattedFee)
    .replace('${biddingFeeExclVAT}', formattedFeeExclVAT);
};

const insertFeesInStructuredText = (
  value: Scalars['JsonField'],
  feeValues: FeeValues
): Scalars['JsonField'] =>
  JSON.parse(JSON.stringify(value), (key, parsedValue) =>
    key === 'value' && typeof parsedValue === 'string'
      ? insertFees(parsedValue, feeValues)
      : parsedValue
  );

type Props = {
  feeAmount: number;
  isBiddingObject: boolean;
  hasDeductibleVat: boolean;
};

function AuctionTips({ feeAmount, isBiddingObject, hasDeductibleVat }: Props) {
  const { data: cmsBiddingData, fetching: isBiddingDataFetching } = useCmsData<
    BiddingQuery,
    BiddingViewData
  >({
    storeKey: 'biddingView',
    query: BiddingDocument,
    options: { pause: !isBiddingObject }
  });
  const { data: cmsFixedPriceData, fetching: isFixedPriceDataFetching } =
    useCmsData<FixedPriceReservationTipsQuery, FixedPriceReservationTipsData>({
      storeKey: 'fixedPriceReservationTips',
      query: FixedPriceReservationTipsDocument,
      options: { pause: isBiddingObject }
    });

  const structuredText = isBiddingObject
    ? cmsBiddingData?.structuredText
    : cmsFixedPriceData?.structuredText;

  if (isBiddingDataFetching || isFixedPriceDataFetching) {
    return (
      <Container>
        <Stack align="center">
          <HeadlineSkeleton />
          {new Array(3).fill('').map((_, index) => {
            return <BodyTextSkeleton key={`skeleton-${index}`} />;
          })}
        </Stack>
      </Container>
    );
  }

  if (!structuredText || isEmptyDocument(structuredText)) {
    return null;
  }

  const formattedFee = feeAmount.toLocaleString('sv', {
    minimumFractionDigits: 0
  });
  const formattedFeeExclVAT = inclVATtoExclVAT(feeAmount).toLocaleString('sv', {
    minimumFractionDigits: 0
  });

  return (
    <Container>
      <StructuredText
        data={{
          value: insertFeesInStructuredText(structuredText.value, {
            formattedFee,
            formattedFeeExclVAT,
            hasDeductibleVat
          })
        }}
        wrapper={TipsWrapper}
      />
    </Container>
  );
}

export default AuctionTips;
